// Pantalla detalle inventario
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import inventoryService from '../../services/inventoryService';
import { useAuth } from '../../context/AuthContext';
import { Alert, Button, Spinner } from '../UI/Index';
import InventoryDetailsList from './InventoryDetailsList';
import UpdateDetailModal from './UpdateDetailModal';
import AddProductModal from './AddProductModal';

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('es-ES', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
};

const getStatusLabel = (status) => {
  switch (status) {
    case 'pending':
      return 'Pendiente';
    case 'started':
      return 'Empezado';
    case 'completed':
      return 'Completado';
    case 'pendingvalidation':
      return 'Pendiente validación';
    default:
      return 'Sin determinar';
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case 'pending':
      return 'bg-yellow-100 text-yellow-800';
    case 'started':
      return 'bg-blue-100 text-blue-800';
    case 'completed':
      return 'bg-green-100 text-green-800';
    case 'pendingvalidation':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const sortDetails = (detailsList) => {
  return [...detailsList].sort((a, b) => {
    const aIsNull = a.qty_input_picked === null;
    const bIsNull = b.qty_input_picked === null;

    if (aIsNull !== bIsNull) return aIsNull ? -1 : 1;

    if (a.zone < b.zone) return -1;
    if (a.zone > b.zone) return 1;

    if (a.status > b.status) return -1;
    if (a.status < b.status) return 1;

    return 0;
  });
};


const InventoryDetail = () => {
  const { inventoryId } = useParams();
  const [inventory, setInventory] = useState(null);
  const [details, setDetails] = useState([]);
  const [filteredDetails, setFilteredDetails] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showAddProductModal, setShowAddProductModal] = useState(false);

  const [barcodeMap, setBarcodeMap] = useState({});
  const [loadingBarcodes, setLoadingBarcodes] = useState(false);
  const [barcodeStats, setBarcodeStats] = useState(null);

  const navigate = useNavigate();
  const { user } = useAuth();

  const handleAddProduct = () => {
    setShowAddProductModal(true);
  };

  const handleCloseAddProductModal = () => {
    setShowAddProductModal(false);
  };

  const handleProductAdded = async () => {
    setShowAddProductModal(false);

    try {
      setLoading(true);
      const response = await inventoryService.getInventoryById(inventoryId);

      if (response.success) {
        setInventory(response.inventory || null);
        setDetails(response.details || []);
        setFilteredDetails(sortDetails(response.details || []));


        if (response.details && response.details.length > 0) {
          await loadBarcodesInBackground(response.details);
        }
      }
    } catch (err) {
      setError(err.message || 'Error al recargar líneas del inventario');
    } finally {
      setLoading(false);
    }
  };

  const loadBarcodesInBackground = async (inventoryDetails) => {
    try {
      setLoadingBarcodes(true);

      const productCodes = [...new Set(inventoryDetails.map(detail => detail.product_code))];

      // console.log(`Precargando barcodes para ${productCodes.length} productos del inventario...`);

      const response = await inventoryService.getBarcodesByProductCodes(productCodes);

      if (response.success && response.barcodes) {
        const mapping = {};
        response.barcodes.forEach(item => {
          mapping[item.barcode] = item.product_code;
        });

        setBarcodeMap(mapping);
        setBarcodeStats(response.stats);

        // console.log(`Precarga completada en ${response.stats.queryTime}: ${response.stats.totalBarcodes} barcodes`);
      }
    } catch (err) {
      console.error('⚠️ Error precargando barcodes (no crítico):', err);
    } finally {
      setLoadingBarcodes(false);
    }
  };

  useEffect(() => {
    const fetchInventoryDetails = async () => {
      try {
        setLoading(true);
        const response = await inventoryService.getInventoryById(inventoryId);

        if (response.success) {
          setInventory(response.inventory || null);
          setDetails(response.details || []);
          setFilteredDetails(sortDetails(response.details || []));


          if (response.details && response.details.length > 0) {
            loadBarcodesInBackground(response.details);
          }
        } else {
          setError(response.message || 'Error al cargar detalles del inventario');
        }
      } catch (err) {
        setError(err.message || 'Error al cargar detalles del inventario');
      } finally {
        setLoading(false);
      }
    };

    if (inventoryId) {
      fetchInventoryDetails();
    }
  }, [inventoryId]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredDetails(sortDetails(details));
      return;
    }

    const searchLower = searchTerm.toLowerCase();
    const searchExact = searchTerm;

    const filtered = details.filter(detail => {
      if (detail.product_code.toLowerCase().includes(searchLower)) {
        return true;
      }

      for (const barcode in barcodeMap) {
        if (barcodeMap[barcode] === detail.product_code) {
          if (barcode === searchExact) {
            return true;
          }
          if (barcode.includes(searchExact)) {
            return true;
          }
        }
      }

      return false;
    });

    setFilteredDetails(sortDetails(filtered));

  }, [searchTerm, details, barcodeMap]);

  const getMatchInfo = (detail, searchTerm) => {
    if (!searchTerm) return null;

    const searchLower = searchTerm.toLowerCase();
    const searchExact = searchTerm;

    if (detail.product_code.toLowerCase().includes(searchLower)) {
      return { type: 'product_code', value: detail.product_code };
    }

    for (const barcode in barcodeMap) {
      if (barcodeMap[barcode] === detail.product_code) {
        if (barcode === searchExact || barcode.includes(searchExact)) {
          return { type: 'barcode', value: barcode };
        }
      }
    }

    return null;
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const handleScannerInput = (e) => {
    if (e.key === 'Enter' && searchTerm.trim()) {
      if (filteredDetails.length === 1) {
        console.log(`🎯 Coincidencia exacta encontrada: ${filteredDetails[0].product_code}`);
        // Abrir modal tras encontrar?
        // handleUpdateDetail(filteredDetails[0]);
      }
    }
  };

  const handleUpdateDetail = (detail) => {
    setSelectedDetail(detail);
    setShowUpdateModal(true);
  };

  const handleCloseUpdateModal = () => {
    setShowUpdateModal(false);
    setSelectedDetail(null);
  };

  const handleDetailUpdated = async () => {
    setShowUpdateModal(false);
    setSelectedDetail(null);

    try {
      setLoading(true);
      const response = await inventoryService.getInventoryById(inventoryId);

      if (response.success) {
        setInventory(response.inventory || null);
        setDetails(response.details || []);
        setFilteredDetails(sortDetails(response.details || []));


        if (response.details && response.details.length > 0) {
          await loadBarcodesInBackground(response.details);
        }
      }
    } catch (err) {
      setError(err.message || 'Error al recargar detalles del inventario');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/dashboard');
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto">
        <Alert type="error" message={error} />
        <div className="mt-4">
          <Button type="secondary" onClick={handleBack}>
            Volver al dashboard
          </Button>
        </div>
      </div>
    );
  }

  if (!inventory) {
    return (
      <div className="container mx-auto">
        <Alert type="warning" message="No se encontró el inventario solicitado" />
        <div className="mt-4">
          <Button type="secondary" onClick={handleBack}>
            Volver al dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto">
      <div className="items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Detalle</h1>
        <Button type="secondary" onClick={handleBack}>
          Volver
        </Button>
      </div>

      <div className="p-6 mb-6 bg-white rounded-lg shadow-md">
        <h2 className="mb-4 text-xl font-semibold">{inventory.inventory_name} (ID: {inventoryId})</h2>

        <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
          <div>
            <span className="block text-sm font-medium text-gray-500">Estado</span>
            <span className={`inline-flex px-2 py-1 mt-1 text-sm font-semibold rounded-full ${getStatusColor(inventory.status)}`}>
              {getStatusLabel(inventory.status)}
            </span>
          </div>

          <div>
            <span className="block text-sm font-medium text-gray-500">Tienda</span>
            <span className="text-gray-900">{inventory.shop}</span>
          </div>

          <div>
            <span className="block text-sm font-medium text-gray-500">Origen</span>
            <span className="text-gray-900">{inventory.origin}</span>
          </div>

          <div>
            <span className="block text-sm font-medium text-gray-500">Tipo</span>
            <span className="text-gray-900">
              {inventory.type === 'expired' ? 'Caducados' : 'Inventario'}
            </span>
          </div>

          <div>
            <span className="block text-sm font-medium text-gray-500">Fecha de creación</span>
            <span className="text-gray-900">{formatDate(inventory.created_at)}</span>
          </div>

          <div>
            <span className="block text-sm font-medium text-gray-500">Fecha de inicio</span>
            <span className="text-gray-900">{formatDate(inventory.available_start_date)}</span>
          </div>
          <div>
            <span className="block text-sm font-medium text-gray-500">Fecha de finalización</span>
            <span className="text-gray-900">{formatDate(inventory.available_end_date)}</span>
          </div>
        </div>
      </div>

      <div className="p-6 bg-white rounded-lg shadow-md">
        <div className="items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Productos</h2>
          {inventory.status !== 'completed' && inventory.origin === 'Tienda' && (
            <Button
              type="primary"
              className={inventory.type === 'expired' ? 'bg-orange-500 hover:bg-orange-600' : ''}
              onClick={handleAddProduct}
            >
              {inventory.type === 'expired' ? 'Añadir Caducados' : 'Añadir Producto'}
            </Button>
          )}
        </div>

        <div className="mb-4">
          <div className="flex items-center space-x-2">
            <div className="flex-1">
              <label htmlFor="product-search" className="block text-sm font-medium text-gray-700 mb-1">
                Buscar producto por EAN/Código
                {loadingBarcodes && (
                  <span className="ml-2 text-xs text-blue-600 animate-pulse">
                    (Espera...cargando barcodes en segundo plano)
                  </span>
                )}
                {barcodeStats && !loadingBarcodes && (
                  <span className="ml-2 text-xs text-green-600">
                    ✅ {barcodeStats.totalBarcodes} barcodes precargados
                  </span>
                )}
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="product-search"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onKeyPress={handleScannerInput}
                  placeholder="Introduce o pica el producto"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm font-mono"
                  autoComplete="off"
                  autoFocus
                />
                {searchTerm && (
                  <button
                    onClick={clearSearch}
                    className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
                    title="Limpiar búsqueda"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          </div>

          {searchTerm && (
            <div className="mt-2 text-sm">
              {filteredDetails.length === 0 ? (
                <span className="text-red-600">
                  ❌ No se encontraron productos que coincidan con "{searchTerm}"
                </span>
              ) : (
                <div className="text-green-600">
                  <span>
                    ✅ {filteredDetails.length} producto{filteredDetails.length !== 1 ? 's' : ''} encontrado{filteredDetails.length !== 1 ? 's' : ''}
                    {filteredDetails.length !== details.length && ` de ${details.length} total`}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {details.length === 0 ? (
          <div className="py-4 text-center text-gray-500">
            No hay productos en este inventario.
          </div>
        ) : (
          <InventoryDetailsList
            details={filteredDetails}
            onUpdateDetail={handleUpdateDetail}
            isCompleted={inventory.status === 'completed'}
            isAdmin={user?.isAdmin}
          />
        )}
      </div>

      {showUpdateModal && selectedDetail && (
        <UpdateDetailModal
          isOpen={showUpdateModal}
          onClose={handleCloseUpdateModal}
          detail={selectedDetail}
          onDetailUpdated={handleDetailUpdated}
          user={user}
        />
      )}

      {showAddProductModal && (
        <AddProductModal
          isOpen={showAddProductModal}
          onClose={handleCloseAddProductModal}
          inventoryId={inventoryId}
          onProductAdded={handleProductAdded}
          inventoryType={inventory.type}
        />
      )}
    </div>
  );
};

export default InventoryDetail;